/**
 * FloatingChat – persistent floating chat widget (like Intercom / Zendesk).
 *
 * Shows a chat bubble at bottom-right with an unread badge.
 * Click to expand a panel with:
 *   - Conversation list (all inquiries)
 *   - Thread view (messages + input)
 *
 * Wired into Socket.IO for real-time messages, typing indicators,
 * and conversation list updates.
 *
 * Renders only for logged-in couple / vendor users.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MessageCircle, X, ArrowLeft, Send, Paperclip } from '../icons';
import { getChatPreview, getInquiryMessages, sendInquiryMessage } from '../services/api';
import { joinInquiry, leaveInquiry, onNewMessage, onTyping, onStopTyping, emitTyping, emitStopTyping, onNotificationAlert, UNREAD_CHANGED_EVENT } from '../services/socket';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import mediaUrl from '../utils/mediaUrl';

export default function FloatingChat() {
  const { user } = useAuth();
  const { t } = useLanguage();

  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState('list');        // 'list' | 'thread'
  const [conversations, setConversations] = useState([]);
  const [activeConvo, setActiveConvo] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msgText, setMsgText] = useState('');
  const [msgFile, setMsgFile] = useState(null);
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [msgLoading, setMsgLoading] = useState(false);
  const [otherTyping, setOtherTyping] = useState(false);
  const [unreadTotal, setUnreadTotal] = useState(0);
  const [pulse, setPulse] = useState(false);

  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const activeConvoIdRef = useRef(null);
  const fileInputRef = useRef(null);

  // ─── Derived unread count ───
  useEffect(() => {
    setUnreadTotal(conversations.filter(c => c.is_unread).length);
  }, [conversations]);

  // ─── Fetch conversation list ───
  const fetchConversations = useCallback(async () => {
    if (!user || (user.role !== 'couple' && user.role !== 'vendor')) return;
    setLoading(true);
    try {
      const res = await getChatPreview();
      setConversations(res.data || []);
    } catch (err) {
      console.error('FloatingChat: fetch conversations failed', err);
    }
    setLoading(false);
  }, [user]);

  // Fetch on mount (for badge) and whenever panel opens
  useEffect(() => {
    if (user && (user.role === 'couple' || user.role === 'vendor')) {
      fetchConversations();
    }
  }, [user, fetchConversations]);

  useEffect(() => {
    if (isOpen) fetchConversations();
  }, [isOpen, fetchConversations]);

  // "Mark all as read" in the bell — refetch so the bubble badge clears too
  useEffect(() => {
    const onChanged = (e) => { if (e.detail?.from !== 'chat') fetchConversations(); };
    window.addEventListener(UNREAD_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(UNREAD_CHANGED_EVENT, onChanged);
  }, [fetchConversations]);

  // ─── Open a conversation thread ───
  const openConvo = async (convo) => {
    // Leave any previously joined room first
    if (activeConvoIdRef.current && activeConvoIdRef.current !== convo.id) {
      leaveInquiry(activeConvoIdRef.current);
    }

    setActiveConvo(convo);
    setView('thread');
    setMsgText('');
    setMsgFile(null);
    setOtherTyping(false);
    activeConvoIdRef.current = convo.id;
    setMsgLoading(true);

    // Mark as read locally
    setConversations(prev => prev.map(c =>
      c.id === convo.id ? { ...c, is_unread: false } : c
    ));

    // Fetch messages (also marks as read on the server)
    try {
      const res = await getInquiryMessages(convo.id);
      setMessages(res.data || []);
      if (convo.is_unread) {
        window.dispatchEvent(new CustomEvent(UNREAD_CHANGED_EVENT, { detail: { from: 'chat' } }));
      }
    } catch (err) {
      console.error('FloatingChat: fetch messages failed', err);
    }
    setMsgLoading(false);

    // Join socket room for real-time updates + typing (server verifies authorization)
    joinInquiry(convo.id);
  };

  // ─── Back to list ───
  const goBack = () => {
    // Leave the inquiry room so we stop receiving messages for it
    if (activeConvoIdRef.current) {
      leaveInquiry(activeConvoIdRef.current);
    }
    setView('list');
    setActiveConvo(null);
    setMessages([]);
    activeConvoIdRef.current = null;
    fetchConversations();
  };

  // ─── Toggle widget ───
  const toggleWidget = () => {
    const opening = !isOpen;
    setIsOpen(opening);
    if (!opening) {
      // Leave any active inquiry room when closing
      if (activeConvoIdRef.current) {
        leaveInquiry(activeConvoIdRef.current);
      }
      setView('list');
      setActiveConvo(null);
      setMessages([]);
      activeConvoIdRef.current = null;
    }
  };

  // ─── Send message ───
  const handleSend = async () => {
    if (!msgText.trim() || !activeConvo || sending) return;
    const text = msgText.trim();
    const file = msgFile;
    setMsgText('');
    setMsgFile(null);
    setSending(true);
    try {
      await sendInquiryMessage(activeConvo.id, text, file || undefined);
      // Re-fetch to ensure consistency (socket will also deliver)
      const res = await getInquiryMessages(activeConvo.id);
      setMessages(res.data || []);
    } catch (err) {
      setMsgText(text);
      setMsgFile(file);
      alert('Failed to send: ' + (err.response?.data?.error || err.message));
    }
    setSending(false);
  };

  // ─── Typing emission ───
  const handleTextChange = (e) => {
    setMsgText(e.target.value);
    if (activeConvoIdRef.current) {
      emitTyping(activeConvoIdRef.current);
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => {
        if (activeConvoIdRef.current) emitStopTyping(activeConvoIdRef.current);
      }, 2000);
    }
  };

  // ─── Socket: real-time new message in active thread ───
  // Only append messages that belong to the currently open conversation.
  useEffect(() => {
    const unsub = onNewMessage(({ inquiryId, message }) => {
      // Strict check: only accept messages for the actively viewed thread
      const activeId = activeConvoIdRef.current;
      if (!activeId || inquiryId !== activeId) return;

      // Belt-and-suspenders: verify message.inquiry_id matches
      if (message.inquiry_id && message.inquiry_id !== activeId) return;

      setMessages(prev => {
        if (prev.find(m => m.id === message.id)) return prev;
        return [...prev, message];
      });
    });
    return () => unsub();
  }, []);

  // ─── Socket: notification alerts → update conversation list ───
  useEffect(() => {
    const unsub = onNotificationAlert((data) => {
      const { inquiryId, preview, senderType, viewing } = data;

      setConversations(prev => {
        const found = prev.find(c => c.id === inquiryId);
        if (!found) {
          // New conversation not in our list – refresh
          fetchConversations();
          return prev;
        }
        const updated = prev.map(c => {
          if (c.id === inquiryId) {
            return {
              ...c,
              last_message: preview,
              last_message_at: new Date().toISOString(),
              last_sender: senderType,
              // `viewing`: the thread is open elsewhere on screen (e.g. the
              // vendor page), and the server did not mark it unread.
              is_unread: !viewing && activeConvoIdRef.current !== inquiryId,
            };
          }
          return c;
        });
        updated.sort((a, b) => {
          const aT = a.last_message_at || a.created_at;
          const bT = b.last_message_at || b.created_at;
          return new Date(bT) - new Date(aT);
        });
        return updated;
      });

      // Pulse bubble if not viewing that conversation
      if (!viewing && (!isOpen || activeConvoIdRef.current !== inquiryId)) {
        setPulse(true);
        setTimeout(() => setPulse(false), 2000);
      }
    });
    return () => unsub();
  }, [isOpen, fetchConversations]);

  // ─── Socket: typing indicators ───
  useEffect(() => {
    const otherType = user?.role === 'vendor' ? 'couple' : 'vendor';
    const unsubT = onTyping(({ inquiryId, senderType }) => {
      if (inquiryId === activeConvoIdRef.current && senderType === otherType) {
        setOtherTyping(true);
      }
    });
    const unsubS = onStopTyping(({ inquiryId, senderType }) => {
      if (inquiryId === activeConvoIdRef.current && senderType === otherType) {
        setOtherTyping(false);
      }
    });
    return () => { unsubT(); unsubS(); };
  }, [user]);

  // ─── Auto-scroll ───
  useEffect(() => {
    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 100);
  }, [messages, otherTyping]);

  // ─── Helpers ───
  const isMine = (msg) => {
    if (user?.role === 'couple' && msg.sender_type === 'couple') return true;
    if (user?.role === 'vendor' && msg.sender_type === 'vendor') return true;
    return false;
  };

  const formatTime = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    const diff = (Date.now() - d) / 1000;
    if (diff < 60) return t('floatingChat.now');
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatMsgTime = (ts) => {
    return new Date(ts).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  // Don't render for non-applicable users
  if (!user || (user.role !== 'couple' && user.role !== 'vendor')) return null;

  return (
    <>
      {/* ───── Floating Bubble ───── */}
      <button
        className={`floating-chat-bubble ${pulse ? 'pulse' : ''} ${isOpen ? 'open' : ''}`}
        onClick={toggleWidget}
        title={isOpen ? t('floatingChat.closeChat') : t('floatingChat.openMessages')}
      >
        {isOpen ? <X size={24} /> : <MessageCircle size={24} />}
        {!isOpen && unreadTotal > 0 && (
          <span className="floating-chat-badge">{unreadTotal > 9 ? '9+' : unreadTotal}</span>
        )}
      </button>

      {/* ───── Chat Panel ───── */}
      {isOpen && (
        <div className="floating-chat-panel">
          {/* Header */}
          <div className="floating-chat-header">
            {view === 'thread' && (
              <button className="floating-chat-back" onClick={goBack}>
                <ArrowLeft size={18} />
              </button>
            )}
            <div className="floating-chat-title">
              {view === 'list' ? t('floatingChat.messages') : (activeConvo?.other_name || t('floatingChat.conversation'))}
            </div>
            <button className="floating-chat-close" onClick={toggleWidget}>
              <X size={18} />
            </button>
          </div>

          {/* Content */}
          <div className="floating-chat-content">
            {view === 'list' ? (
              /* ─── Conversation List ─── */
              loading ? (
                <div className="floating-chat-empty">
                  <div className="spinner" style={{ width: 30, height: 30 }} />
                </div>
              ) : conversations.length === 0 ? (
                <div className="floating-chat-empty">
                  <MessageCircle size={40} style={{ color: '#d1d5db', marginBottom: 12 }} />
                  <p>{t('floatingChat.noConversations')}</p>
                  <span>{t('floatingChat.noConversationsDesc')}</span>
                </div>
              ) : (
                <div className="floating-chat-list">
                  {conversations.map(convo => (
                    <div
                      key={convo.id}
                      className={`floating-chat-item ${convo.is_unread ? 'unread' : ''}`}
                      onClick={() => openConvo(convo)}
                    >
                      <div className="floating-chat-item-avatar">
                        {convo.other_photo ? (
                          <img src={mediaUrl(convo.other_photo)} alt="" />
                        ) : (
                          <span>{(convo.other_name || '?')[0]}</span>
                        )}
                        {convo.is_unread && <div className="floating-chat-item-dot" />}
                      </div>
                      <div className="floating-chat-item-body">
                        <div className="floating-chat-item-top">
                          <span className="floating-chat-item-name">{convo.other_name}</span>
                          <span className="floating-chat-item-time">
                            {formatTime(convo.last_message_at || convo.created_at)}
                          </span>
                        </div>
                        <div className="floating-chat-item-preview">
                          {convo.last_message
                            ? ((convo.last_sender === (user.role === 'couple' ? 'couple' : 'vendor') ? t('floatingChat.youPrefix') : '')
                              + (convo.last_message.length > 50
                                ? convo.last_message.substring(0, 50) + '…'
                                : convo.last_message))
                            : convo.initial_message
                              ? (convo.initial_message.length > 50
                                ? convo.initial_message.substring(0, 50) + '…'
                                : convo.initial_message)
                              : t('floatingChat.noMessagesYet')
                          }
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )
            ) : (
              /* ─── Thread View ─── */
              <>
                <div className="floating-chat-messages">
                  {msgLoading ? (
                    <div className="floating-chat-empty">
                      <div className="spinner" style={{ width: 30, height: 30 }} />
                    </div>
                  ) : messages.length === 0 ? (
                    <div className="floating-chat-empty">
                      <p>{t('floatingChat.noMessagesYet')}</p>
                      <span>{t('floatingChat.sendFirst')}</span>
                    </div>
                  ) : (
                    messages.map(msg => (
                      <div key={msg.id} className={`fchat-msg ${isMine(msg) ? 'mine' : 'theirs'}`}>
                        <div className="fchat-msg-bubble">
                          <p>{msg.message}</p>
                          {msg.attachment_url && (
                            <a href={mediaUrl(msg.attachment_url)} target="_blank" rel="noopener noreferrer" className="fchat-msg-attach">
                              📎 {msg.attachment_name || t('floatingChat.attachment')}
                            </a>
                          )}
                          <span className="fchat-msg-time">{formatMsgTime(msg.created_at)}</span>
                        </div>
                      </div>
                    ))
                  )}
                  {otherTyping && (
                    <div className="fchat-msg theirs">
                      <div className="fchat-msg-bubble typing">
                        <span className="typing-dots"><span /><span /><span /></span>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Input bar */}
                <div className="floating-chat-input">
                  <button className="fchat-attach-btn" onClick={() => fileInputRef.current?.click()} title={t('floatingChat.attachFile')}>
                    <Paperclip size={16} />
                  </button>
                  <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                    onChange={(e) => setMsgFile(e.target.files[0] || null)}
                  />
                  <input
                    type="text"
                    placeholder={t('floatingChat.typeMessage')}
                    value={msgText}
                    onChange={handleTextChange}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                  />
                  <button className="fchat-send-btn" onClick={handleSend} disabled={sending || !msgText.trim()}>
                    <Send size={16} />
                  </button>
                </div>
                {msgFile && (
                  <div className="fchat-file-indicator">
                    📎 {msgFile.name}
                    <button onClick={() => setMsgFile(null)}>&times;</button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
